import { createRef, forwardRef, useContext, useEffect, useImperativeHandle, useMemo } from 'react';

import {
    Checkbox,
    FormControl,
    FormControlLabel,
    ListItemText,
    MenuItem,
    Radio,
    styled,
    Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ClassContext } from '../../context/ClassContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { RecordingRestrictMode, type Permission } from '../classroom/types';
import { IconTooltip } from '../common/Tooltip/IconTooltip';
import { ControlledInput, ControlledSelect } from '../forms';
import ControlledRadioGroup from '../forms/ControlledRadioGroup';

const StyledForm = styled('form')`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const PublicPermissionHint = styled(Typography)`
    font-size: 10px;
    line-height: 20px;
    margin-top: 4px;
    color: ${(props) => props.theme.palette.text.secondary};
`;

const defaultValues = {
    name: '',
    permission: 'ADMINS',
    studentGroups: []
};

export type FormData = {
    name: string;
    permission: string;
    restrictBy: string;
    studentGroups: number[];
};

type EditRecordingFormProps = {
    onSubmitValid: (data: FormData) => Promise<void>;
    dialogOpen?: boolean;
    defaultFormData?: FormData;
    showNameField?: boolean;
};

export type ExposedFormMethods = {
    triggerSubmit: () => void;
};

type RestrictionModeOption = {
    mode: RecordingRestrictMode;
    label: string;
    disabled?: boolean;
    hint?: string;
};

export const EditRecordingForm = forwardRef<ExposedFormMethods, EditRecordingFormProps>(
    (props: EditRecordingFormProps, ref) => {
        const { dialogOpen, onSubmitValid, defaultFormData, showNameField } = props;
        const { t } = useTranslation();
        const { data: orgSettings } = useOrgSettings();

        const { adminSettings, classData } = useContext(ClassContext);

        const classGroups = useMemo(() => classData?.classGroups ?? [], [classData]);

        const orgRestrictBySessions = orgSettings?.featureSettings?.restrictSessionsRecordingsByGroups;
        const orgRestriction = orgRestrictBySessions ? RecordingRestrictMode.Session : RecordingRestrictMode.None;

        const defaults = useMemo(
            () => defaultFormData ?? { ...defaultValues, restrictBy: orgRestriction },
            [defaultFormData, orgRestriction]
        );

        // Note - isValid must be deconstructed here or it will always be false:
        // https://github.com/react-hook-form/react-hook-form/issues/2755#issuecomment-962274624
        const {
            handleSubmit,
            control,
            formState: { isValid },
            reset,
            watch
        } = useForm<FormData>({ defaultValues, shouldUnregister: true });
        const formRef: any = createRef();

        const onSubmit = async (data: FormData) => {
            if (!isValid) {
                return;
            }

            await onSubmitValid(data);
            reset(data);
        };

        const submitForm = () => {
            formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        };

        // Expose the submit function to the parent - this is so dialogs can trigger submit on confirm.
        useImperativeHandle(ref, () => ({
            triggerSubmit: () => {
                submitForm();
            }
        }));

        const selectedPermission = watch('permission');
        const restrictBy = watch('restrictBy');
        const formSelectedGroups = watch('studentGroups');

        useEffect(() => {
            reset(defaults);
            // Note, dialogOpen is a dependency here so that it resets the form data when opened / closed.
        }, [reset, defaults, dialogOpen]);

        const excludePublicPermission = orgSettings?.featureSettings?.options?.recordingsDisablePublicOption;
        const permissionItems: Permission[] = [
            ...(!excludePublicPermission ? ['PUBLIC' as Permission] : []),
            'ROSTER',
            'ADMINS'
        ];

        const sectionGroups = useMemo(() => classGroups.filter((group) => group.is_section), [classGroups]);
        const groups = useMemo(() => classGroups.filter((group) => !group.is_section), [classGroups]);

        const restrictionModes = useMemo<RestrictionModeOption[]>(
            () => [
                {
                    mode: RecordingRestrictMode.Site,
                    label: t('lti:recordings.student-access.restrict-by-site'),
                    hint: t('lti:recordings.student-access.restrict-by-site-hint')
                },
                { mode: RecordingRestrictMode.None, label: t('lti:recordings.student-access.restrict-by-none') },
                {
                    mode: RecordingRestrictMode.Session,
                    label: t('lti:recordings.student-access.restrict-by-session'),
                    disabled: !orgRestrictBySessions
                },
                {
                    mode: RecordingRestrictMode.Group,
                    label: t('lti:recordings.student-access.restrict-by-group'),
                    disabled: groups.length < 1
                }
            ],
            [t, orgRestrictBySessions, groups]
        );

        const showStudentAccessControls =
            adminSettings?.options.bulkRecordingOperations && selectedPermission === 'ROSTER';

        return (
            <StyledForm ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <FormControl sx={{ display: showNameField ? 'flex' : 'none' }} fullWidth>
                    <ControlledInput type="text" name="name" control={control} label={t('class:name')} />
                </FormControl>
                <FormControl fullWidth>
                    <ControlledSelect name="permission" label={t('lti:recordings.permission')} control={control}>
                        {permissionItems.map((permissionItem, c) => (
                            <MenuItem
                                disabled={permissionItem === 'PUBLIC' && excludePublicPermission}
                                key={permissionItem}
                                value={permissionItem}
                            >
                                {t(`lti:recordings.permissions.${permissionItem.toLowerCase()}`)}
                            </MenuItem>
                        ))}
                    </ControlledSelect>
                    {selectedPermission === 'PUBLIC' && (
                        <PublicPermissionHint>{t('lti:recordings.permissions.public-warn')}</PublicPermissionHint>
                    )}
                </FormControl>

                {showStudentAccessControls && (
                    <FormControl fullWidth>
                        <ControlledRadioGroup
                            label={t('lti:recordings.student-access.restrict-by')}
                            name="restrictBy"
                            control={control}
                        >
                            {restrictionModes.map((item) => {
                                const labelTxt = t(
                                    `lti:recordings.student-access.restrict-by-${String(item.mode).toLowerCase()}`
                                );
                                const labelSuffix = !item.hint ? null : (
                                    <IconTooltip sx={{ marginTop: '-3px' }}>{item.hint}</IconTooltip>
                                );
                                const label = (
                                    <Box sx={{ display: 'flex' }}>
                                        <Typography>{labelTxt}</Typography>
                                        {labelSuffix}
                                    </Box>
                                );
                                return (
                                    <FormControlLabel
                                        disabled={Boolean(item?.disabled)}
                                        key={item.mode}
                                        name={`mode[${item.mode}]`}
                                        value={item.mode}
                                        control={<Radio />}
                                        label={label}
                                    />
                                );
                            })}
                        </ControlledRadioGroup>

                        <ControlledSelect
                            sx={{ opacity: restrictBy === RecordingRestrictMode.Group ? '1' : '0.5' }}
                            disabled={restrictBy !== RecordingRestrictMode.Group}
                            multiple
                            name="studentGroups"
                            label={t('lti:recordings.student-access.groups')}
                            control={control}
                            renderValue={(selected) => (
                                <>
                                    {((selected as number[]) ?? [])
                                        .map(
                                            (groupId) =>
                                                classGroups.find((f) => f.c_student_group_id === groupId)?.label
                                        )
                                        .join(', ')}
                                </>
                            )}
                        >
                            {groups.map((group) => (
                                <MenuItem key={group.lms_group_id} value={group.c_student_group_id}>
                                    <Checkbox checked={formSelectedGroups?.includes(group.c_student_group_id)} />
                                    <ListItemText primary={group.label} />
                                </MenuItem>
                            ))}
                            {sectionGroups.map((group) => (
                                <MenuItem key={group.lms_section_id} value={group.c_student_group_id}>
                                    <Checkbox checked={formSelectedGroups?.includes(group.c_student_group_id)} />
                                    <ListItemText primary={group.label} />
                                </MenuItem>
                            ))}
                        </ControlledSelect>
                    </FormControl>
                )}
            </StyledForm>
        );
    }
);
